import React, { useState, useEffect, useRef } from "react";
import {
  Loader2,
  ChevronLeft,
  Compass,
  ArrowRight,
  Check,
  RotateCw,
  XCircle,
  CheckCircle2,
  Save,
  MoveDiagonal,
} from "lucide-react";
import { useTranslation } from "react-i18next";
import { showToast } from "utils/toast";
import { formatMac } from "./Manufacturing/constants";
import { parseFactoryLine, parseFactoryLines } from "utils/factoryTest";
import {
  initCalibrationState,
  reduceCalibrationState,
  CALIBRATION_STEPS,
  CAL_TARGETS,
  calStepIndex,
  calInPosition,
} from "utils/calibration";
import { dateLocale } from "i18n";

const sleep = (ms) => new Promise((r) => setTimeout(r, ms));
const fmtVec = (v, fn) => (Array.isArray(v) ? v.map(fn).join(", ") : "—");

const stepMeta = (slug) => CALIBRATION_STEPS.find((s) => s.slug === slug);

// Live 3-axis g-meter. The axis under test is emphasised; when it reads near ±1 g
// (and the others are flat) it turns green. Gyro-rest has no target axis.
const AXIS_LABELS = ["X", "Y", "Z"];
const LiveGauge = ({ step, mg }) => {
  const { t } = useTranslation();
  const target = CAL_TARGETS[step];
  const inPos = calInPosition(step, mg);
  return (
    <div className="mt-4 space-y-2.5">
      {AXIS_LABELS.map((label, i) => {
        const g = mg[i] / 1000;
        const isTarget = target && target.axis === i;
        const onTarget = isTarget && Math.abs(mg[i] - target.sign * 1000) < 90;
        // Map g ∈ [-1.2, 1.2] to 0–100% across the track.
        const pct = Math.max(0, Math.min(100, ((g + 1.2) / 2.4) * 100));
        // Target marker position for this axis.
        const targetPct = isTarget
          ? ((target.sign * 1 + 1.2) / 2.4) * 100
          : null;
        const barColor = onTarget
          ? "#16A34A"
          : isTarget
          ? "#E07A3A"
          : "#9CA3AF";
        return (
          <div key={label} className={isTarget ? "" : "opacity-45"}>
            <div className="mb-1 flex items-center justify-between text-[11px]">
              <span
                className={`font-medium ${
                  isTarget ? "text-[#111827]" : "text-[#9CA3AF]"
                }`}
              >
                {isTarget
                  ? t("deviceTools.calibration.gauge.axisTarget", {
                      axis: label,
                    })
                  : label}
              </span>
              <span
                className="font-mono font-semibold"
                style={{ color: barColor }}
              >
                {g >= 0 ? "+" : ""}
                {g.toFixed(2)} g
              </span>
            </div>
            <div className="relative h-2 rounded-full bg-[#F3F4F6]">
              {/* centre (0 g) */}
              <div className="absolute left-1/2 top-0 h-full w-px bg-[#E5E7EB]" />
              {/* target tick for the axis under test */}
              {targetPct != null && (
                <div
                  className="absolute top-[-2px] h-3 w-0.5 rounded bg-[#16A34A]"
                  style={{ left: `${targetPct}%` }}
                />
              )}
              {/* live marker */}
              <div
                className="absolute top-1/2 h-3 w-3 -translate-x-1/2 -translate-y-1/2 rounded-full ring-2 ring-white transition-all duration-75"
                style={{ left: `${pct}%`, backgroundColor: barColor }}
              />
            </div>
          </div>
        );
      })}
      {inPos === true ? (
        <p className="flex items-center justify-center gap-1.5 pt-1 text-[12px] font-semibold text-[#16A34A]">
          <Check size={13} strokeWidth={3} />
          {t("deviceTools.calibration.gauge.inPosition")}
        </p>
      ) : target ? (
        <p className="pt-1 text-center text-[11px] text-[#9CA3AF]">
          {t("deviceTools.calibration.gauge.rotateToTarget", {
            value: target.sign > 0 ? "+1.00" : "−1.00",
          })}
        </p>
      ) : (
        <p className="pt-1 text-center text-[11px] text-[#9CA3AF]">
          {t("deviceTools.calibration.gauge.holdFlat")}
        </p>
      )}
    </div>
  );
};

/**
 * Guided 7-step IMU calibration (firmware `calibrate`). The firmware pushes the
 * prompts; the operator repositions the device and clicks Next, collection runs
 * ~8s per step, then a result is shown to save. Mirrors FactoryTestFlow: a cursor
 * scan folds NDJSON events into state, commands are sent with kick-if-silent.
 */
const CalibrationFlow = ({
  deviceMac,
  send,
  kick,
  receivedData,
  liveLineRef,
  connected,
  commandsLocked,
  onExit,
}) => {
  const { t } = useTranslation();
  const [state, setState] = useState(initCalibrationState);
  const [runId, setRunId] = useState(0);
  const [saving, setSaving] = useState(false);
  const [live, setLive] = useState(null); // { step, mg } — coalesced gauge value
  const [waitedForLive, setWaitedForLive] = useState(false);
  const scanPosRef = useRef(receivedData.length);

  // Mirror "have we seen any calibration event yet" for the start kick loop.
  const sawEventRef = useRef(false);
  useEffect(() => {
    sawEventRef.current =
      state.started || !!state.prompt || !!state.status || !!state.error;
  }, [state.started, state.prompt, state.status, state.error]);

  // Passive scanner: fold new lines into calibration state.
  useEffect(() => {
    if (receivedData.length <= scanPosRef.current) return;
    const events = [];
    for (let i = scanPosRef.current; i < receivedData.length; i++) {
      events.push(...parseFactoryLines(receivedData[i]?.data));
    }
    scanPosRef.current = receivedData.length;
    if (events.length) {
      setState((prev) => events.reduce(reduceCalibrationState, prev));
    }
  }, [receivedData]);

  const sendCmd = async (cmd) => {
    await send(cmd).catch(() => {});
    await sleep(150);
    await kick?.().catch?.(() => {});
  };

  // Start on mount / retry. Always `calibrate` — the button IS "start a run", and
  // this is robust (a stale device status can't send us down a resync path that
  // hangs). If the device is mid-run, calibrate re-enters at step 1.
  useEffect(() => {
    scanPosRef.current = receivedData.length;
    sawEventRef.current = false;
    setState(initCalibrationState());
    setSaving(false);
    let cancelled = false;
    (async () => {
      await sleep(60); // StrictMode-safe: cancel a double-mount before it sends
      if (cancelled) return;
      await sendCmd("calibrate");
      // Kick-if-silent until the first calibration event lands.
      for (let i = 0; i < 8 && !cancelled; i++) {
        await sleep(1500);
        if (cancelled || sawEventRef.current) break;
        await kick?.().catch?.(() => {});
      }
      // Never got a reply — surface a retry rather than hang on "Starting…".
      if (!cancelled && !sawEventRef.current) {
        setState((prev) => ({ ...prev, error: { reason: "no_response" } }));
      }
    })();
    return () => {
      cancelled = true;
    };
    // eslint-disable-next-line
  }, [runId]);

  // Exit — abort the run unless we just saved.
  const exit = ({ abort = true } = {}) => {
    if (abort) send("calabort").catch(() => {});
    onExit();
  };

  // Advance from a prompt. Optimistically show collecting so Next disables and
  // the bar appears immediately (the real `collecting` events refine it).
  const onNext = async () => {
    if (!state.prompt) return;
    const step = state.prompt.step;
    setState((prev) => ({
      ...prev,
      collecting: { step, done: 0, total: prev.samples ?? 1000 },
      prompt: null,
    }));
    await sendCmd("calnext");
  };

  const onSave = async () => {
    if (saving) return;
    setSaving(true);
    await sendCmd("calsave");
  };

  // On a successful save, toast and leave (no abort needed). v0.5.63 returns the
  // save time; epochS === 0 means the device clock wasn't synced.
  useEffect(() => {
    if (state.saved?.ok) {
      const epochS = state.saved.epochS;
      showToast.success(
        epochS
          ? t("deviceTools.calibration.savedOn", {
              date: new Date(epochS * 1000).toLocaleDateString(
                dateLocale("en-GB"),
                {
                  day: "numeric",
                  month: "short",
                  year: "numeric",
                }
              ),
            })
          : epochS === 0
          ? t("deviceTools.calibration.savedUnsynced")
          : t("deviceTools.calibration.saved")
      );
      exit({ abort: false });
    }
    // eslint-disable-next-line
  }, [state.saved]);

  const retry = () => setRunId((n) => n + 1);

  // ── Phase + progress derivation ───────────────────────────────────
  const phase = state.error
    ? "error"
    : state.result
    ? "result"
    : state.collecting
    ? "collecting"
    : state.prompt
    ? "positioning"
    : "starting";

  // Live gauge: paint the latest live line on requestAnimationFrame (~60 Hz),
  // not per incoming line (~100 Hz) — the data is lossy, so coalescing is fine.
  // It streams only during positioning; hide it otherwise.
  const positioning = phase === "positioning";
  const promptStep = state.prompt?.step;
  useEffect(() => {
    if (!positioning || !liveLineRef) {
      setLive(null);
      setWaitedForLive(false);
      return undefined;
    }
    setWaitedForLive(false);
    // If no live reading has arrived after a few seconds, the firmware likely
    // doesn't emit the `live` stream — stop implying it's coming.
    const noLiveTimer = setTimeout(() => setWaitedForLive(true), 3500);
    let raf;
    let last = null;
    const paint = () => {
      const s = liveLineRef.current;
      if (s && s !== last) {
        last = s;
        const ev = parseFactoryLine(s);
        if (ev?.t === "live" && Array.isArray(ev.ax_mg)) {
          setLive({ step: ev.step, mg: ev.ax_mg });
        }
      }
      raf = requestAnimationFrame(paint);
    };
    raf = requestAnimationFrame(paint);
    return () => {
      cancelAnimationFrame(raf);
      clearTimeout(noLiveTimer);
    };
  }, [positioning, promptStep, liveLineRef]);

  const looksGood =
    positioning &&
    live &&
    live.step === promptStep &&
    calInPosition(live.step, live.mg) === true;

  // Derive the 1-based step position from the step SLUG, not prompt.index —
  // firmware changed index from 1-based (v0.5.43) to 0-based (v0.5.63), and the
  // slug's position in CALIBRATION_STEPS is a stable source of truth either way.
  const currentStep = state.prompt?.step ?? state.collecting?.step ?? null;
  const currentIndex = state.result
    ? 8
    : currentStep
    ? calStepIndex(currentStep)
    : 0;

  const collectPct =
    state.collecting && state.collecting.total
      ? Math.min(
          100,
          Math.round((state.collecting.done / state.collecting.total) * 100)
        )
      : null;

  const total = state.prompt?.total ?? state.steps ?? 7;

  return (
    <div className="mx-auto flex h-full max-w-xl flex-col">
      {/* Header */}
      <div className="mb-4 flex items-center gap-3">
        <button
          onClick={() => exit({ abort: true })}
          className="flex h-8 w-8 flex-shrink-0 items-center justify-center rounded-lg border border-[#E5E7EB] bg-white text-[#6B7280] transition duration-150 hover:bg-[#F9FAFB] hover:text-[#374151]"
          title={t("deviceTools.calibration.cancel")}
        >
          <ChevronLeft size={16} />
        </button>
        <div className="min-w-0 flex-1">
          <p className="text-sm font-semibold text-[#111827]">
            {t("deviceTools.calibration.title")}
          </p>
          <p className="truncate text-[11px] text-[#9CA3AF]">
            {deviceMac
              ? formatMac(deviceMac)
              : t("deviceTools.calibration.device")}
            {state.fw ? ` · ${state.fw}` : ""}
            {phase !== "result" && phase !== "starting"
              ? ` · ${t("deviceTools.calibration.stepOf", {
                  current: currentIndex,
                  total,
                })}`
              : ""}
          </p>
        </div>
        {state.persisted && phase !== "result" && (
          <span className="flex-shrink-0 rounded bg-[#FEF3E7] px-2 py-0.5 text-[10px] font-semibold text-[#E07A3A]">
            {t("deviceTools.calibration.overwritingSaved")}
          </span>
        )}
      </div>

      <div className="flex min-h-0 flex-1 flex-col gap-3 overflow-y-auto">
        {/* Step checklist */}
        {phase !== "starting" && phase !== "error" && (
          <div className="grid grid-cols-7 gap-1">
            {CALIBRATION_STEPS.map((s, i) => {
              const idx = i + 1;
              const done = state.result || idx < currentIndex;
              const active = !state.result && idx === currentIndex;
              return (
                <div
                  key={s.slug}
                  title={s.label}
                  className={`flex flex-col items-center gap-1 rounded-md py-1.5 text-[9px] font-medium ${
                    active
                      ? "bg-[#FFF3ED] text-[#E07A3A]"
                      : done
                      ? "text-[#16A34A]"
                      : "text-[#C4C8CE]"
                  }`}
                >
                  {done ? (
                    <Check size={12} strokeWidth={3} />
                  ) : (
                    <span
                      className={`h-2.5 w-2.5 rounded-full ${
                        active ? "bg-[#E07A3A]" : "bg-[#E5E7EB]"
                      }`}
                    />
                  )}
                  {s.label}
                </div>
              );
            })}
          </div>
        )}

        {/* Starting */}
        {phase === "starting" && (
          <div className="flex flex-1 flex-col items-center justify-center gap-3 text-center">
            <Loader2 size={26} className="animate-spin text-[#E07A3A]" />
            <p className="text-sm text-[#6B7280]">
              {commandsLocked
                ? t("deviceTools.shared.waitingForConsole")
                : t("deviceTools.calibration.starting")}
            </p>
          </div>
        )}

        {/* Error */}
        {phase === "error" && (
          <div className="flex flex-1 flex-col items-center justify-center gap-3 text-center">
            <XCircle size={26} className="text-[#DC2626]" />
            <p className="max-w-sm text-sm font-medium text-[#B91C1C]">
              {state.error.reason === "session_running"
                ? t("deviceTools.calibration.errorSessionRunning")
                : state.error.reason === "no_response"
                ? t("deviceTools.calibration.errorNoResponse")
                : t("deviceTools.calibration.errorGeneric", {
                    reason: state.error.reason,
                  })}
            </p>
            <div className="flex items-center gap-2">
              <button
                onClick={() => exit({ abort: true })}
                className="rounded-[10px] border border-[#E5E7EB] bg-white px-4 py-2 text-sm font-medium text-[#374151] transition duration-150 hover:bg-[#F9FAFB]"
              >
                {t("common.actions.back")}
              </button>
              <button
                onClick={retry}
                disabled={!connected}
                className="flex items-center gap-1.5 rounded-[10px] border border-[#E5E7EB] bg-white px-4 py-2 text-sm font-semibold text-[#374151] transition duration-150 hover:bg-[#F9FAFB] disabled:opacity-50"
              >
                <RotateCw size={14} />
                {t("common.actions.retry")}
              </button>
            </div>
          </div>
        )}

        {/* Positioning — friendly instruction + live g-meter + Next */}
        {phase === "positioning" && (
          <div className="rounded-xl border border-[#FEEAD9] bg-[#FFF9F4] p-5">
            <div className="text-center">
              <MoveDiagonal size={24} className="mx-auto text-[#E07A3A]" />
              <p className="mx-auto mt-2 max-w-sm text-sm font-semibold text-[#111827]">
                {stepMeta(state.prompt.step)?.instruction ||
                  t("deviceTools.calibration.positionFallback")}
              </p>
            </div>

            {live && live.step === state.prompt.step ? (
              <LiveGauge step={live.step} mg={live.mg} />
            ) : waitedForLive ? (
              <p className="mx-auto mt-4 max-w-xs text-center text-xs text-[#9CA3AF]">
                {t("deviceTools.calibration.liveUnsupported")}
              </p>
            ) : (
              <p className="mt-4 text-center text-xs text-[#9CA3AF]">
                {t("deviceTools.calibration.waitingLive")}
              </p>
            )}

            <button
              onClick={onNext}
              disabled={!connected}
              className="mt-4 flex w-full items-center justify-center gap-1.5 rounded-[10px] px-4 py-2.5 text-sm font-semibold text-white transition duration-150 disabled:opacity-50"
              style={{
                background: looksGood
                  ? "linear-gradient(135deg, #22C55E 0%, #16A34A 100%)"
                  : "linear-gradient(135deg, #E8854A 0%, #D66B2E 100%)",
              }}
            >
              {looksGood && <Check size={15} />}
              {t("common.actions.next")}
              {!looksGood && <ArrowRight size={15} />}
            </button>
          </div>
        )}

        {/* Collecting — progress */}
        {phase === "collecting" && (
          <div className="rounded-xl border border-[#F3F4F6] bg-white p-5 text-center">
            <Loader2
              size={22}
              className="mx-auto animate-spin text-[#E07A3A]"
            />
            <p className="mt-2 text-sm font-semibold text-[#111827]">
              {t("deviceTools.calibration.holdStill")}
            </p>
            <p className="text-xs text-[#9CA3AF]">
              {CALIBRATION_STEPS.find((s) => s.slug === state.collecting.step)
                ?.label || state.collecting.step}
            </p>
            <div className="mt-3">
              <div className="h-1.5 w-full overflow-hidden rounded-full bg-[#F3F4F6]">
                <div
                  className="h-full rounded-full bg-[#E07A3A] transition-all duration-300"
                  style={{ width: `${collectPct ?? 6}%` }}
                />
              </div>
              {collectPct != null && (
                <p className="mt-1 font-mono text-[11px] text-[#9CA3AF]">
                  {collectPct}%
                </p>
              )}
            </div>
          </div>
        )}

        {/* Result — coefficients + save */}
        {phase === "result" && (
          <div className="rounded-xl border border-[#F3F4F6] bg-white p-5">
            <div
              className={`mb-4 flex items-center gap-2 rounded-lg border px-3 py-2 ${
                state.result.valid
                  ? "border-[#BBF7D0] bg-[#F0FDF4]"
                  : "border-[#FECACA] bg-[#FEF2F2]"
              }`}
            >
              {state.result.valid ? (
                <CheckCircle2
                  size={16}
                  className="flex-shrink-0 text-[#16A34A]"
                />
              ) : (
                <XCircle size={16} className="flex-shrink-0 text-[#DC2626]" />
              )}
              <p
                className={`text-[13px] ${
                  state.result.valid ? "text-[#15803D]" : "text-[#B91C1C]"
                }`}
              >
                {state.result.valid
                  ? t("deviceTools.calibration.resultValid")
                  : t("deviceTools.calibration.resultInvalid")}
              </p>
            </div>

            <dl className="space-y-2.5 text-[13px]">
              <div className="flex items-start justify-between gap-3">
                <dt className="text-[#6B7280]">
                  {t("deviceTools.calibration.gyroBias")}
                </dt>
                <dd className="text-right font-mono text-[#111827]">
                  {fmtVec(state.result.gyroBiasMdps, (v) => v)} mdps
                </dd>
              </div>
              <div className="flex items-start justify-between gap-3">
                <dt className="text-[#6B7280]">
                  {t("deviceTools.calibration.accelScale")}
                </dt>
                <dd className="text-right font-mono text-[#111827]">
                  {fmtVec(
                    state.result.accelScalePpm,
                    (v) => `×${(v / 1e6).toFixed(4)}`
                  )}
                </dd>
              </div>
              <div className="flex items-start justify-between gap-3">
                <dt className="text-[#6B7280]">
                  {t("deviceTools.calibration.accelBias")}
                </dt>
                <dd className="text-right font-mono text-[#111827]">
                  {fmtVec(
                    state.result.accelBiasUg,
                    (v) => `${(v / 1000).toFixed(2)}`
                  )}{" "}
                  mg
                </dd>
              </div>
            </dl>
            <p className="mt-2 text-[11px] text-[#9CA3AF]">
              {state.persisted
                ? t("deviceTools.calibration.healthyHintOverwrite")
                : t("deviceTools.calibration.healthyHint")}
            </p>

            <div className="mt-4 flex items-center gap-2">
              <button
                onClick={() => exit({ abort: true })}
                className="rounded-[10px] border border-[#E5E7EB] bg-white px-4 py-2.5 text-sm font-medium text-[#374151] transition duration-150 hover:bg-[#F9FAFB]"
              >
                {t("deviceTools.calibration.discard")}
              </button>
              <button
                onClick={onSave}
                disabled={saving}
                className="flex flex-1 items-center justify-center gap-1.5 rounded-[10px] px-4 py-2.5 text-sm font-semibold text-white transition duration-150 disabled:opacity-50"
                style={{
                  background:
                    "linear-gradient(135deg, #E8854A 0%, #D66B2E 100%)",
                }}
              >
                {saving ? (
                  <Loader2 size={15} className="animate-spin" />
                ) : (
                  <Save size={15} />
                )}
                {t("deviceTools.calibration.saveToDevice")}
              </button>
            </div>
          </div>
        )}

        {/* Intro caption while running */}
        {(phase === "positioning" || phase === "collecting") && (
          <p className="flex items-center justify-center gap-1.5 text-[11px] text-[#9CA3AF]">
            <Compass size={12} />
            {t("deviceTools.calibration.runDuration")}
          </p>
        )}
      </div>
    </div>
  );
};

export default CalibrationFlow;
